import { useMemo } from 'react';
import {
  JobMutationAction,
  JobMutationState,
  TransformedJobItem,
} from '@worker/types/myJobs.type';
import { ChatEntityType } from '@th-types/chat.type';
import { buildJobPageUrl } from '@worker/helpers/my-jobs-helpers';
import { useDismissJobMutation } from '@worker/hooks/useDismissJobMutation';
import { useSaveJobMutation } from '@worker/hooks/useSaveJobMutation';
import { useUnsaveJobMutation } from '@worker/hooks/useUnsaveJobMutation';
import useNotification from '@hooks/useNotification';
import { useChat } from '@hooks/useChat';

/**
 * Centralizes the 4 toolbar actions (dismiss / save toggle / share / message)
 * + the shared mutation state used to render per-action loading indicators.
 *
 * Apply / un-apply stay outside this hook because they have caller-specific
 * pre-apply flows (e.g., profile/distance checks) and modal-close coupling.
 */
export function useRecommendationActions() {
  const dismissMutation = useDismissJobMutation();
  const saveMutation = useSaveJobMutation();
  const unsaveMutation = useUnsaveJobMutation();
  const { showSuccessNotification, showErrorNotification } = useNotification();
  const { openChat } = useChat();

  const handleDismiss = (recommendation: TransformedJobItem) => {
    if (!recommendation.id) return;
    dismissMutation.mutate(recommendation.id);
  };

  const handleDismissById = (jobRecommendationId: number) => {
    dismissMutation.mutate(jobRecommendationId);
  };

  const handleBookmark = (recommendation: TransformedJobItem) => {
    const jobId = recommendation.job?.id;
    if (!jobId) return;
    if (recommendation.savedAt) {
      unsaveMutation.mutate(jobId);
    } else {
      saveMutation.mutate(jobId);
    }
  };

  const handleShare = async (recommendation: TransformedJobItem) => {
    const jobDetailsUrl = buildJobPageUrl(recommendation.job?.publicUrl);
    if (!jobDetailsUrl) {
      showErrorNotification('Unable to get job URL', { title: 'Share Failed' });
      return;
    }
    try {
      await navigator.clipboard.writeText(jobDetailsUrl);
      showSuccessNotification('Job link copied to clipboard', {
        title: 'Link Copied!',
      });
    } catch {
      showErrorNotification('Unable to copy link to clipboard', {
        title: 'Copy Failed',
      });
    }
  };

  const handleMessage = (recommendation: TransformedJobItem) => {
    // Chat availability is gated in the toolbar — this is a safety guard.
    const { jobAssignmentId, job } = recommendation;
    if (!jobAssignmentId || !job) return;
    openChat({
      jobAssignmentId,
      entity: {
        id: job.company.id,
        type: ChatEntityType.COMPANY,
        name: job.company.name,
        profileImageUrl: job.company.profileImageUrl,
      },
      jobInfo: {
        jobId: job.id,
        jobName: job.title,
      },
    });
  };

  const mutationState = useMemo<JobMutationState>(() => {
    if (saveMutation.isPending) {
      return {
        action: JobMutationAction.SAVE,
        targetId: saveMutation.variables ?? null,
      };
    }
    if (unsaveMutation.isPending) {
      return {
        action: JobMutationAction.UNSAVE,
        targetId: unsaveMutation.variables ?? null,
      };
    }
    if (dismissMutation.isPending) {
      return {
        action: JobMutationAction.DISMISS,
        targetId: dismissMutation.variables ?? null,
      };
    }
    return { action: null, targetId: null };
  }, [
    saveMutation.isPending,
    saveMutation.variables,
    unsaveMutation.isPending,
    unsaveMutation.variables,
    dismissMutation.isPending,
    dismissMutation.variables,
  ]);

  return {
    handleDismiss,
    handleDismissById,
    handleBookmark,
    handleShare,
    handleMessage,
    mutationState,
  };
}

export default useRecommendationActions;
